import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { PropsWithChildren } from 'react';
import { RenderTrackerProvider } from './render-tracker';
import { StableAnaltyicsProvider } from './stable-analytics';
import { StableChoreographerProvider } from '@atlassian/post-office-context/choreographer';
import { StableContextProvider } from './stable-context';
import { StablePlacementProvider } from './stable-placement';
import { StableSubscriptionConfigProvider } from './stable-graphql-subscription';
import type { SubscriptionConfig } from './stable-graphql-subscription/types';
import { StableRouteProvider } from './stable-route';
import type { StableProvidersProps } from './types';
import type { StableProvider } from '../types';

export { PerformanceTracking, PerformanceTrackingSegment } from './performance-tracking';

/**
 * Feature gate name for GraphQL subscriptions in Post Office placements.
 * When disabled, subscriptions will not be enabled even if configured.
 */
// https://switcheroo.atlassian.com/ui/gates/8299833f-7cf4-453b-bd5b-16f3bcf81ddb/key/post-office_graphql_subscriptions_enabled
const POST_OFFICE_GRAPHQL_SUBSCRIPTIONS_FEATURE_GATE = 'post-office_graphql_subscriptions_enabled';

/**
 * Optional provider that wraps subscription providers only if feature gate is enabled.
 */
const OptionalPostOfficeSubscriptionsProvider = ({
	children,
	subscription,
}: PropsWithChildren<{
	subscription?: SubscriptionConfig;
}>) => {
	const isPostOfficeSubscriptionsFeatureGateEnabled = FeatureGates.checkGate(
		POST_OFFICE_GRAPHQL_SUBSCRIPTIONS_FEATURE_GATE,
	);

	if (isPostOfficeSubscriptionsFeatureGateEnabled) {
		return (
			<StableSubscriptionConfigProvider subscription={subscription}>
				{children}
			</StableSubscriptionConfigProvider>
		);
	}

	return children;
};

export const StableProviders: StableProvider<StableProvidersProps> = ({
	children,
	context,
	placement,
	route,
	choreographer,
	subscription,
}) => {
	return (
		<StableContextProvider context={context}>
			<StablePlacementProvider placement={placement}>
				<StableRouteProvider route={route}>
					<StableAnaltyicsProvider>
						<OptionalPostOfficeSubscriptionsProvider subscription={subscription}>
							<StableChoreographerProvider
								choreographer={choreographer}
								placementId={placement.placementId}
							>
								<RenderTrackerProvider placementId={placement.placementId}>
									{children}
								</RenderTrackerProvider>
							</StableChoreographerProvider>
						</OptionalPostOfficeSubscriptionsProvider>
					</StableAnaltyicsProvider>
				</StableRouteProvider>
			</StablePlacementProvider>
		</StableContextProvider>
	);
};
